import { AjaxForm } from "@neonjungle/birdseed/forms/ajax";

class CMSignupAjaxForm extends AjaxForm {
	constructor(form, message) {
		super(form, message);
		this.turnstileContainer = form.querySelector("[data-cm-signup-turnstile]");
		this.turnstileWidgetId = null;
		this.turnstileRendered = false;
		this.turnstileToken = null;
		this.turnstileWaitResolve = null;
		this.turnstileScriptTimeoutId = null;
	}

	fetchAndReplace() {
		if (!this.turnstileContainer || !window.TURNSTILE_SITE_KEY || this.turnstileToken) {
			super.fetchAndReplace();
			return;
		}

		if (this.turnstileWaitResolve) {
			// A challenge is already running for an earlier submit; it will
			// send the request once the token arrives.
			return;
		}

		// Show the Turnstyle widget and wait for a token before sending the request.
		// We do this to avoid showing the ugly Cloudflare widget to users who do not use
		// the subscribe form.
		this.toggleSubmitButtonState();
		this.waitForTurnstileToken().then(() => {
			this.toggleSubmitButtonState();
			super.fetchAndReplace();
		});
	}

	waitForTurnstileToken() {
		return new Promise(resolve => {
			this.turnstileWaitResolve = resolve;

			if (this.turnstileRendered) {
				window.turnstile.reset(this.turnstileWidgetId);
			} else {
				this.renderTurnstile();
			}
		});
	}

	renderTurnstile() {
		if (this.turnstileRendered || !window.TURNSTILE_SITE_KEY) return;

		if (!window.turnstile) {
			// The Turnstile API script loads async; render once it arrives.
			// If it fails to load, hand back a null token so the request is
			// still sent and the server's error response is shown rather
			// than leaving the form hanging.
			const script = document.querySelector(
				'script[src*="challenges.cloudflare.com/turnstile"]'
			);
			if (script) {
				script.addEventListener("load", () => this.renderTurnstile(), { once: true });
				script.addEventListener("error", () => this.setTurnstileToken(null), { once: true });
				// Safety net for a script that failed before these listeners
				// were attached, or that never finishes loading: hand back a
				// null token so the request is still sent.
				this.turnstileScriptTimeoutId = setTimeout(
					() => this.setTurnstileToken(null),
					5000
				);
			} else {
				this.setTurnstileToken(null);
			}
			return;
		}

		// The script is available, so the load timeout is no longer needed.
		if (this.turnstileScriptTimeoutId) {
			clearTimeout(this.turnstileScriptTimeoutId);
			this.turnstileScriptTimeoutId = null;
		}

		let widget = this.turnstileContainer.querySelector(".cf-turnstile");
		if (!widget) {
			widget = document.createElement("div");
			widget.classList.add("cf-turnstile");
			widget.setAttribute("data-action", "signup");
			this.turnstileContainer.appendChild(widget);
		}

		// Show the container before rendering so the widget sizes correctly.
		this.turnstileContainer.classList.add("is-rendered");
		this.turnstileWidgetId = window.turnstile.render(widget, {
			sitekey: window.TURNSTILE_SITE_KEY,
			action: widget.getAttribute("data-action") || "signup",
			callback: token => this.setTurnstileToken(token),
			"error-callback": () => this.setTurnstileToken(null)
		});
		this.turnstileRendered = true;
	}

	setTurnstileToken(token) {
		if (this.turnstileScriptTimeoutId) {
			clearTimeout(this.turnstileScriptTimeoutId);
			this.turnstileScriptTimeoutId = null;
		}

		this.turnstileToken = token;

		if (this.turnstileWaitResolve) {
			const resolve = this.turnstileWaitResolve;
			this.turnstileWaitResolve = null;
			resolve();
		}
	}

	// Turnstile tokens are single-use, so after a failed attempt the widget
	// is reset (staying visible) so a fresh token is ready for the next try.
	resetTurnstile() {
		if (!this.turnstileRendered || !window.turnstile) return;

		this.turnstileToken = null;
		window.turnstile.reset(this.turnstileWidgetId);
	}

	prepareForNextSignup() {
		const emailInput = this.form.querySelector('input[type="email"]');
		if (emailInput) {
			emailInput.value = "";
		}

		if (this.turnstileRendered && window.turnstile) {
			window.turnstile.remove(this.turnstileWidgetId);
			this.turnstileWidgetId = null;
			this.turnstileRendered = false;
		}
		this.turnstileToken = null;
		if (this.turnstileContainer) {
			this.turnstileContainer.classList.remove("is-rendered");
		}
	}

	afterFetch(responseJson) {
		super.afterFetch(responseJson);
		if (responseJson.success) {
			this.prepareForNextSignup();
		} else {
			this.resetTurnstile();
		}
	}

	toggleSubmitButtonState() {
		super.toggleSubmitButtonState();
		if (this.submitButton) {
			this.submitButton.classList.toggle("is-loading", this.submitButton.disabled);
		}
	}
}

export default function emailSubscribeForms() {
	const $subForms = document.querySelectorAll("[data-cm-signup-form]");
	$subForms.forEach($form => {
		let message = $form.querySelector("[data-cm-signup-form-message]");
		new CMSignupAjaxForm($form, message);
	});
}
